import { Modal, Pressable, Platform } from "react-native"
import { List } from "./list"
import { Text, TextInput } from "./core"
import { formatDateForDisplay, sharedStyles, trpc } from "@src/utils"
import { useState } from "react"
import Icon from "@react-native-vector-icons/material-design-icons"
import { Show, useObservable } from "@legendapp/state/react"
import { ar } from "@src/i18n/ar"
import { cn, priceFromDBFormat } from "@src/utils"
import { Header } from "./Header"
import { useDebounce } from "use-debounce"

type Props = {
  orderId?: number
  onChange: (id?: number) => void
  className?: string
}

export function OrderPicker({ orderId, className, onChange }: Props) {
  const ordersModal$ = useObservable(false)
  const order = trpc.orders.get.useQuery({ id: orderId! }, { enabled: !!orderId })

  return (
    <>
      <Pressable className={cn("h-11 bg-gray-50 border border-gray-300 rounded px-4 justify-center active:opacity-50", className)} onPress={() => ordersModal$.set(true)}>
        <Text className={`text-left ${order.data?.orderNumber ? "text-gray-900" : "text-gray-500"}`}>
          {order.data?.orderNumber ? `Order #${order.data.orderNumber}` : ar.selectOrder}
        </Text>
      </Pressable>

      <Show if={ordersModal$}>
        <OrderPickModal hide={() => ordersModal$.set(false)} onSelectionChange={onChange} />
      </Show>
    </>
  )
}

function OrderPickModal({ onSelectionChange, hide }: { onSelectionChange: (id: number) => void; hide: () => void }) {
  const [search, setSearch] = useState("")
  const [debouncedSearch] = useDebounce(search, 500)
  const orders = trpc.orders.list.useQuery({ orderNumber: debouncedSearch, status: ["delivered"], limit: 20 }, { enabled: !!debouncedSearch && debouncedSearch.length >= 3 })

  return (
    <Modal animationType="slide" presentationStyle="pageSheet" onRequestClose={hide}>
      <Header
        title={ar.orderLookup}
        withoutInsets={Platform.OS === "ios"}
        canGoBack={false}
        right={
          <Pressable onPress={hide} className="h-full px-2 justify-center">
            <Icon name="close" size={25} />
          </Pressable>
        }
      />
      <TextInput className="w-full mt-2 max-w-content-wide mx-auto" placeholder={ar.orderNumber} value={search} onChangeText={setSearch} />

      <List
        data={orders.data?.items ?? []}
        keyExtractor={(item) => String(item.id)}
        contentContainerStyle={sharedStyles.contentContainer}
        renderItem={({ item: order }) => {
          return (
            <Pressable
              className="py-3 px-4 border-b border-gray-300"
              onPress={() => {
                onSelectionChange(order.id)
                hide()
              }}
            >
              <Text className="font-bold">
                {ar.orderNumber}: {order.orderNumber}
              </Text>
              <Text className="font-bold">
                {ar.amount}: {priceFromDBFormat(order.total)}
              </Text>
              <Text className="font-bold">
                {ar.customers}: {order.customer?.name ?? "-"}
              </Text>
              <Text className="font-bold">
                {ar.date}: {formatDateForDisplay(order.createdAt)}
              </Text>
            </Pressable>
          )
        }}
      />
    </Modal>
  )
}
